import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Checkbox, Col, Form, Input, Modal, Radio, Row, Select, Skeleton, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { CloseCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../atoms';
import { TitleModal } from '../../molecules/modal/TitleModal';
import { ExplorerPreview, ProductPreviewInterface } from '../../molecules/preview/explore/ExplorePreview';
import { StyledForm, StyledPreview } from './form/StyledForm';
import {
  HomeWidgetsRecord,
  postCreateHomeWidgetsRepository,
  postUpdateHomeWidgetsRepository,
} from '../../../repository/mytm/home/homeWidgetsRepository';
import { getProductsInCategoryRepository } from '../../../repository/mytm/ecom/ecomRepository';
import { EcomProductContext } from '../../../repository/context/ecomProductContext';
import { HomeGroupKey, HomeTypeExplorer, HomeWidgetStatus, HomeWidgetsSubmitForm } from '../../../types/home';
import { toPayloadHomeWidgets } from '../../../utils/transfer/home';
import { logging } from '../../../utils/log';
import { appStyles } from '../../../styles';
import { DEFAULT_HOME, HOME_EXPLORERS } from '../../../constants/home';
import { MESSAGE_ERROR } from '../../../constants/message';
import { HOME } from '../../../constants/define/translation';
import HomeTileRecordId from './HomeTileRecordId';
import { onValidator } from '../../../utils/helper';
import { CountryContext } from '../../../repository/context/countryContext';

interface HomeExplorerModalProps {
  isOpenModal: boolean;
  onClose: () => void;
  onRefresh: () => void;
  record?: HomeWidgetsRecord;
}

const DEFAULT_EXPLORER = {
  name: undefined,
  heading: undefined,
  title: DEFAULT_HOME.EXPLORE.TITLE,
  description: undefined,
  ctaText: DEFAULT_HOME.EXPLORE.CTA_TEXT,
  ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK,
  contentId: undefined,
  contentFormat: undefined,
  contentType: undefined,
  productId: undefined,
  backgroundImage: DEFAULT_HOME.EXPLORE.BACKGROUND_IMAGE,
  type: HOME_EXPLORERS[0].value,
  countries: DEFAULT_HOME.EXPLORE.COUNTRIES,
};

const { Option } = Select;

export const HomeExplorerModal = (props: HomeExplorerModalProps) => {
  const { t } = useTranslation(HOME);
  const [form] = Form.useForm();
  const ecomProductContext = useContext(EcomProductContext);
  const countryContext = useContext(CountryContext);

  const { isOpenModal, onClose, onRefresh, record } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isMemberExclusive, setIsMemberExclusive] = useState(false);
  const [isDrillOfTheWeek, setIsDrillOfTheWeek] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<ProductPreviewInterface>();

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });
  const defaultCountryValues: any[] = countryContext?.countryList?.map(item => {
    return item?.code;
  });

  const resetFormProduct = useCallback(() => {
    // ecomProductContext.updateProductList([]);
    setCurrentProduct(undefined);
  }, []);

  const onSaveHomeWidgets = useCallback(
    async (values: HomeWidgetsSubmitForm) => {
      try {
        setIsLoading(true);
        const payload = toPayloadHomeWidgets({
          ...values,
          status: record?.status || HomeWidgetStatus.INACTIVE,
          groupKey: record?.groupKey || HomeGroupKey.HOME_EXPLORER,
          show: record?.show,
          title: isMemberExclusive ? DEFAULT_HOME.EXPLORE.TITLE_PRODUCT : DEFAULT_HOME.EXPLORE.TITLE,
          backgroundImage: isMemberExclusive
            ? DEFAULT_HOME.EXPLORE.BACKGROUND_IMAGE_PRODUCT
            : DEFAULT_HOME.EXPLORE.BACKGROUND_IMAGE,
          productId: isMemberExclusive ? values.productId : undefined,
          contentId: !isMemberExclusive ? values.contentId : undefined,
          contentType: !isMemberExclusive ? values.contentType : undefined,
          contentFormat: !isMemberExclusive ? values.contentFormat : undefined,
          countries: values?.countries.toString(),
          extraData: record?.extraData || {},
        });
        if (record) {
          await postUpdateHomeWidgetsRepository(record?.id, payload);
          message.success(t('message.updateHomeExplorerSuccess'));
        } else {
          await postCreateHomeWidgetsRepository({
            ...payload,
            extraData: JSON.stringify(payload.extraData),
          });
          message.success(t('message.createHomeExplorerSuccess'));
        }
        onRefresh();
        onClose();
        form.setFieldsValue(DEFAULT_EXPLORER);
      } catch (error: any) {
        logging(error);
        if (record) {
          message.error(error?.errorMessage || t('message.updateHomeExplorerError'));
        } else {
          message.error(error?.errorMessage || t('message.createHomeExplorerError'));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onRefresh, record, onClose, isMemberExclusive],
  );

  const handleGetEcomProduct = useCallback(
    async (country?: string) => {
      let productList: any;
      try {
        const dataProductEcom: any = await getProductsInCategoryRepository({ country });
        if (dataProductEcom) {
          const productEcomList = dataProductEcom?.hits.map((product: any, index: number) => ({
            index,
            id: product?.representedProduct?.id,
            image: product?.image?.link,
            name: product?.productName || '',
          }));
          ecomProductContext.updateProductList(productEcomList);
          productList = productEcomList;
        } else {
          resetFormProduct();
        }
      } catch (error) {
        resetFormProduct();
        message.error(MESSAGE_ERROR.M02);
      }
      setCurrentProduct((oldValue: any) => {
        if (record && !oldValue) {
          return productList.find((v: any) => v.id === record?.productId);
        }
        return oldValue;
      });
    },
    [record],
  );

  const handleSelectExplorerType = useCallback(
    async (value: string) => {
      setIsMemberExclusive(value === HomeTypeExplorer.MEMBER_ONLY_EXCLUSIVE);
      setIsDrillOfTheWeek(!!(record && value === HomeTypeExplorer.DRILL_OF_THE_WEEK));
      if (value === HomeTypeExplorer.MEMBER_ONLY_EXCLUSIVE) {
        await handleGetEcomProduct(record?.countries || 'USA');
        form.setFieldsValue({
          countries: record?.countries || 'USA',
        });

        form.setFieldsValue({
          ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK_PRODUCT,
        });
      } else {
        if (!record) {
          form.setFieldsValue({
            countries: defaultCountryValues,
          });
        }

        form.setFieldsValue({
          ctaLink: DEFAULT_HOME.EXPLORE.CTA_LINK,
        });
        if (record && value === HomeTypeExplorer.DRILL_OF_THE_WEEK) {
          form.setFieldsValue({
            contentId: record.contentId,
            contentFormat: record.contentFormat,
            contentType: record.contentType,
          });
        }
        resetFormProduct();
      }
    },
    [form, record, resetFormProduct, handleGetEcomProduct],
  );

  const onHandleSelectProduct = useCallback(
    (value: string) => {
      const { productList } = ecomProductContext;
      if (!productList || productList.length === 0) {
        resetFormProduct();
        return;
      }
      const product = ecomProductContext.productList.find((x: any) => x.id === value);
      if (product) {
        setCurrentProduct(product);
      }
    },
    [ecomProductContext.productList, resetFormProduct],
  );

  useEffect(() => {
    if (record) {
      const formValue = {
        name: record?.name,
        type: record?.type,
        heading: record?.heading,
        title: record?.title,
        description: record?.description,
        ctaText: record?.ctaText,
        ctaLink: record?.ctaLink,
        contentId: record?.contentId,
        contentFormat: record?.contentFormat,
        contentType: record?.contentType,
        productId: record?.productId,
        backgroundImage: record?.backgroundImage,
        countries:
          record?.contentType === HomeTypeExplorer.MEMBER_ONLY_EXCLUSIVE
            ? record?.countries
            : record?.countries?.split(','),
      };

      form.setFieldsValue(formValue);
      handleSelectExplorerType(record?.type || '');
    } else {
      form.setFieldsValue(DEFAULT_EXPLORER);
      resetFormProduct();
      setIsDrillOfTheWeek(false);
      setIsMemberExclusive(false);
    }
  }, [record, form, handleSelectExplorerType, resetFormProduct]);

  const onChangeCountry = (event: any) => {
    const value = event?.target?.value;
    handleGetEcomProduct(value);
    resetFormProduct();
    form.setFieldsValue({
      productId: undefined,
    });
  };

  return (
    <StyledModal
      title={<TitleModal title={t('header.titleExplorer')} />}
      centered
      visible={isOpenModal}
      width={840}
      footer={null}
      onCancel={() => {
        onClose();
      }}
      maskClosable={false}
      closeIcon={<CloseCircleOutlined />}
      forceRender
    >
      <SpinLoading isLoading={isLoading} absolute>
        <Form layout="vertical" form={form} onFinish={onSaveHomeWidgets} name="form-home-explore">
          <Row>
            <StyledPreview span={9}>
              <ExplorerPreview currentProduct={currentProduct} isMemberExclusive={isMemberExclusive} />
            </StyledPreview>
            <StyledForm span={15}>
              <div className="form-scroll">
                {record?.id && <HomeTileRecordId id={record?.id} />}
                {record?.id && (
                  <HomeTileRecordId
                    id={`${process.env.REACT_APP_DEEP_PATH}/vertical?verticalId=${record?.id}`}
                    isDeepLink
                  />
                )}
                <Form.Item
                  name="name"
                  label={t('form.cardName')}
                  rules={[{ required: true, message: t('validate.nameBlank') }]}
                >
                  <Input placeholder={t('form.cardName')} maxLength={255} />
                </Form.Item>
                <SelectOption
                  modeForm
                  name="type"
                  label={t('form.type')}
                  rules={[{ required: true, message: t('validate.homeTypeBank') }, onValidator(HOME_EXPLORERS)]}
                  values={HOME_EXPLORERS}
                  onChange={handleSelectExplorerType}
                />
                <Form.Item
                  name="countries"
                  label={t('form.countries')}
                  rules={[{ required: true, message: t('validate.countryBlank') }]}
                >
                  {isMemberExclusive ? (
                    <Radio.Group options={countryOptions} onChange={onChangeCountry} />
                  ) : (
                    <Checkbox.Group options={countryOptions} />
                  )}
                </Form.Item>
                <Form.Item
                  name="heading"
                  label={t('form.labelOfCard')}
                  rules={[{ required: true, message: t('validate.labelBlank') }]}
                >
                  <Input placeholder={t('form.labelOfCardPlaceholder')} maxLength={255} />
                </Form.Item>
                {isMemberExclusive && (
                  <Form.Item label={t('form.productId')}>
                    {!ecomProductContext.productList && (
                      <>
                        <Skeleton active paragraph={{ rows: 0 }} />
                      </>
                    )}
                    {ecomProductContext.productList && ecomProductContext.productList.length === 0 && (
                      <Form.Item name="productId">
                        <Input placeholder={t('form.productIdPlaceholder')} maxLength={255} />
                      </Form.Item>
                    )}
                    {ecomProductContext.productList && ecomProductContext.productList.length > 0 && (
                      <Form.Item name="productId">
                        <Select
                          showSearch
                          placeholder={t('form.productIdPlaceholder')}
                          optionFilterProp="children"
                          filterOption={(input, option: any) =>
                            option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={onHandleSelectProduct}
                          allowClear
                        >
                          {ecomProductContext.productList.map((item: any, index: number) => (
                            <Option value={item?.id || ''} key={item.index} className="option-content-product-ecom">
                              <div className="thumb-product-ecom">
                                <img src={item?.image} alt="product" />
                              </div>
                              <Tooltip title={item.name}>
                                <span className="content-product-ecom-id">{item.id}</span>
                              </Tooltip>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Form.Item>
                )}

                {!isMemberExclusive && (
                  <Row>
                    <Col span={7}>
                      <Form.Item name="contentId" label={t('form.content')}>
                        <Input
                          placeholder={t('form.contentId')}
                          maxLength={50}
                          readOnly={isDrillOfTheWeek}
                          disabled={isDrillOfTheWeek}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7} offset={1} className="">
                      <Form.Item name="contentType" label=" ">
                        <Input
                          placeholder={t('form.contentType')}
                          maxLength={50}
                          readOnly={isDrillOfTheWeek}
                          disabled={isDrillOfTheWeek}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7} offset={1} className="">
                      <Form.Item name="contentFormat" label=" ">
                        <Input
                          placeholder={t('form.contentFormat')}
                          maxLength={50}
                          readOnly={isDrillOfTheWeek}
                          disabled={isDrillOfTheWeek}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col span={11}>
                    <Form.Item name="ctaText" label={t('form.button')}>
                      <Input placeholder={t('form.buttonText')} maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col span={11} offset={2} className="">
                    <Form.Item name="ctaLink" label=" ">
                      <Input placeholder={t('form.buttonLink')} maxLength={200} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              <ButtonActionPage
                text={record ? t('btnUpdateCard') : t('btnCreateCard')}
                id="1"
                className="btn-submit-form-home"
                htmlType="submit"
                icon={!record ? <FileAddOutlined /> : undefined}
              />
            </StyledForm>
          </Row>
        </Form>
      </SpinLoading>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  & > .ant-modal-content {
    ${appStyles.commonModalContentStyle}
  }

  .site-card-wrapper {
    ${appStyles.background.modalBgGray}
    & .card-item {
      text-align: center;
      padding: 1rem;
    }
  }

  & h4.ant-typography {
    ${appStyles.color.black}
  }

  .ant-modal-header {
    padding-left: 0;
  }

  .card-item .ant-image {
    cursor: pointer;
  }

  & .card-up-to-date {
    & .ant-image {
      padding: 0px;
      margin-left: 12px;
      & img {
        width: 92%;
        padding: 5px 0 0px 0;
      }
    }
  }
`;
